import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [orgMenus, setOrgMenus] = useState(null); // null = no org-level gating configured
  const [adminMenus, setAdminMenus] = useState(null); // null = not a platform admin / not loaded
  const [adminNavPerms, setAdminNavPerms] = useState(null); // raw role menu perms (Admin Role Master Section 2), keyed per nav item

  const loadPerms = useCallback(async () => {
    try { const { data } = await api.get("/me/permissions"); setPermissions(data.menus || {}); }
    catch { setPermissions({}); }
  }, []);

  const loadOrgMenus = useCallback(async () => {
    try { const { data } = await api.get("/me/org-menus"); setOrgMenus(data.menus ?? null); }
    catch { setOrgMenus(null); }
  }, []);

  const loadAdminMenus = useCallback(async () => {
    try { const { data } = await api.get("/me/admin-menus"); setAdminMenus(data.menus ?? null); setAdminNavPerms(data.nav_perms ?? null); }
    catch { setAdminMenus(null); setAdminNavPerms(null); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadPerms();
        await loadOrgMenus();
        await loadAdminMenus();
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadPerms, loadOrgMenus, loadAdminMenus]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) setAuthToken(data.token);
    setUser(data.user);
    await loadPerms();
    await loadOrgMenus();
    await loadAdminMenus();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAuthToken(null);
    setUser(false);
    setPermissions({});
    setOrgMenus(null);
    setAdminMenus(null);
    setAdminNavPerms(null);
  };

  // Re-pull permission sources when the tab regains focus so authorisation
  // changes made by an admin apply without the user logging out and back in.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && user) { loadPerms(); loadOrgMenus(); loadAdminMenus(); }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [user, loadPerms, loadOrgMenus, loadAdminMenus]);

  const isSuperAdmin = !!(user && user.is_platform_admin && (user.platform_role || "super_admin") === "super_admin");

  const can = (menu, action = "view") => {
    if (!user) return false;
    if (isSuperAdmin) return true; // hardcoded: SaaS Admin always has every action on every page
    if (user.role === "customer") return false;
    if (user.role === "owner") return true;
    return !!(permissions[menu] && permissions[menu][action]);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, setUser, permissions, can, orgMenus, isSuperAdmin, adminMenus, adminNavPerms }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
